/**
 * LandingPage — unauthenticated marketing page (rebuilt s89 from the Claude Design reference:
 * docs/claude_design/Organized Estate Landing.dc.html + README.md).
 *
 * Single-file by design (self-contained marketing surface): module-level helpers (DonutRing,
 * DoneBadge, TodoDot, CheckMark, Eyebrow) + data arrays + section-commented JSX. Inline styles
 * carry the exact brand hexes / type scale from the design; Tailwind arbitrary classes carry
 * layout + responsive breakpoints (inline styles can't express media queries).
 *
 * Essentials note (s89): the hero card + Estate Ready Score spotlight mirror the 10
 * ESSENTIAL_GROUPS in shared/essentials-spec.ts (7 done / 3 to-do, shown illustratively).
 */
import { SAGE, CLAY, WARM, FONT } from "@shared/brand";
import { useEffect, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { PublicNav, PublicFooter, CONTAINER, consumePendingScroll } from "@/components/public/PublicLayout";
import { PricingSection } from "@/components/public/PricingSection";
import { AdvisorSection } from "@/components/public/AdvisorSection";
import { HOW_IT_WORKS } from "@/components/public/how-it-works-data";
import { Seo } from "@/components/public/Seo";
import { StructuredData } from "@/components/public/StructuredData";
import { ArrowRight, ClipboardCheck, Compass, FileText, Folder, Gauge, Heart, ListChecks, Lock, Route, ScanLine } from "lucide-react";

// ───────────────────────── helpers ─────────────────────────

function Eyebrow({ children, color = SAGE[400] }: { children: ReactNode; color?: string }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color, marginBottom: 16 }}>
      {children}
    </div>
  );
}

/** Estate Ready Score donut — 70% filled (dasharray 263.9 / dashoffset 79.2). */
function DonutRing({ size, strokeWidth, withOfTen = false }: { size: number; strokeWidth: number; withOfTen?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ flex: "none" }} aria-hidden="true">
      <circle cx="50" cy="50" r="42" fill="none" stroke="#E4E9DC" strokeWidth={strokeWidth} />
      <circle cx="50" cy="50" r="42" fill="none" stroke={SAGE[600]} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray="263.9" strokeDashoffset="79.2" transform="rotate(-90 50 50)" />
      <text x="50" y={withOfTen ? 46 : 50} textAnchor="middle" dominantBaseline="central" fontFamily={FONT.serif} fontSize={withOfTen ? 30 : 26} fontWeight="500" fill={WARM.ink}>7</text>
      {withOfTen && <text x="50" y="64" textAnchor="middle" dominantBaseline="central" fontFamily="DM Sans,sans-serif" fontSize="10" fill={WARM.taupe}>of 10</text>}
    </svg>
  );
}

/** Filled sage circle with a white check — a "done" essential / step. */
function DoneBadge({ d, check, strokeWidth = 3.2 }: { d: number; check: number; strokeWidth?: number }) {
  return (
    <span style={{ width: d, height: d, borderRadius: "50%", background: SAGE[600], display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
      <svg width={check} height={check} viewBox="0 0 24 24" fill="none" stroke={WARM.white} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
    </span>
  );
}

/** Outline circle — a "to-do" essential / step. */
function TodoDot({ d, color = "#CDC6B6", width = 1.6 }: { d: number; color?: string; width?: number }) {
  return <span style={{ width: d, height: d, borderRadius: "50%", border: `${width}px solid ${color}`, flex: "none" }} />;
}

/** Standalone sage check (no circle) for feature / pricing bullet lists. */
function CheckMark({ size = 18, strokeWidth = 1.8 }: { size?: number; strokeWidth?: number }) {
  return (
    <span style={{ color: SAGE[600], flex: "none", marginTop: 2, display: "flex" }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
    </span>
  );
}

// ───────────────────────── data ─────────────────────────

const FEATURES: { icon: typeof Compass; title: string; body: ReactNode }[] = [
  { icon: Compass, title: "Guided, category by category", body: <>Nine categories, and a <strong style={{ fontWeight: 500 }}>Start Here</strong> router that always tells you the single next thing to do. No blank page, no overwhelm.</> },
  { icon: ScanLine, title: "Upload, and it fills itself in", body: "Drop in a bank statement or policy and the details are read and entered for you. Less typing, fewer mistakes, more done in a sitting." },
  { icon: Gauge, title: "Estate Ready Score", body: "Ten essentials that tell you, at a glance, how prepared you really are — and exactly what to do next to move the needle." },
  { icon: Route, title: "An executor engine, not a checklist", body: "A guided path through settling the estate — with a letter generator, document generator, and pre-filled government forms along the way." },
  { icon: Lock, title: "Secure Information File", body: "The truly sensitive things — passwords, PINs — stay on your own device, never on our servers. Add a passphrase and the file is encrypted with AES-256; without one it opens directly." },
  { icon: ClipboardCheck, title: "What's Next", body: "See exactly what your executor will still be missing — the gaps you'd never think of, surfaced before they become their problem." },
];

const SPOTLIGHT_ESSENTIALS: { label: string; done: boolean }[] = [
  { label: "Your Will & executor", done: true },
  { label: "Your finances", done: true },
  { label: "Life insurance", done: true },
  { label: "Property", done: true },
  { label: "Funeral wishes", done: true },
  { label: "Beneficiaries", done: true },
  { label: "Estate lawyer & key advisors", done: true },
  { label: "Debts & liabilities", done: false },
  { label: "Guardian for your children", done: false },
  { label: "Documents & IDs", done: false },
];

const EXECUTOR_POINTS: { icon: typeof Compass; title: string; body: string }[] = [
  { icon: ListChecks, title: "A guided path, not a checklist", body: "Province-specific steps in the right order, each one explained — never 69 duties dropped on them at once." },
  { icon: FileText, title: "Letters & forms, already filled", body: "Notification letters and government forms come pre-filled from the plan — ready to review and sign." },
  { icon: Folder, title: "Everything you prepared, in their hands", body: "Accounts, documents, wishes and where to find it all — handed over the moment it's needed, nothing lost." },
];

const SHOW_ADVISOR_BAND = true;

// ───────────────────────── shared style fragments ─────────────────────────

const card: React.CSSProperties = { background: WARM.white, border: "1px solid #E7E1D5", borderRadius: 16, padding: "36px 34px" };

// ───────────────────────── page ─────────────────────────

export default function LandingPage() {
  const [, navigate] = useLocation();
  // Auth-aware CTAs (s90): the landing page renders for both auth states. When logged
  // in, the nav/hero point into the app instead of sign-in/sign-up.
  const { isAuthenticated } = useAuth();

  // When a section link is clicked from another public page (contact/security), the
  // shared nav/footer stash the target id and navigate here; scroll to it on mount.
  useEffect(() => {
    const id = consumePendingScroll();
    if (id) {
      requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, []);

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const go = (path: string) => () => {
    navigate(path);
  };

  return (
    <div style={{ background: WARM.cream, overflowX: "hidden", fontFamily: FONT.sans, color: WARM.charcoal }}>
      <style>{`::selection{background:${SAGE[100]}}`}</style>

      {/* Per-route head tags + the three JSON-LD graphs (s216 phase 1, items 3 and 7). */}
      <Seo path="/" />
      <StructuredData />

      <PublicNav />

      {/* ============ HERO ============ */}
      <section style={{ background: `radial-gradient(110% 80% at 50% 0%, ${SAGE[50]} 0%, ${WARM.cream} 60%)`, padding: "80px 32px 36px" }}>
        <div className={CONTAINER} style={{ textAlign: "center" }}>
          <Eyebrow color={SAGE[600]}>Planner + executor, one place</Eyebrow>
          <h1 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 55, lineHeight: 1.07, letterSpacing: "-0.015em", color: WARM.ink, margin: "0 0 24px", textWrap: "balance" }}>
            Protect the people you love.
          </h1>
          <p style={{ fontSize: 18, lineHeight: 1.62, color: "#514D44", margin: "0 auto 34px", maxWidth: 640 }}>
            You organize everything in one place. When the time comes, your executor sees exactly what to do — every step, already prepared.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center" }}>
            <button onClick={go(isAuthenticated ? "/dashboard" : "/signup")} style={{ fontSize: 16, color: WARM.white, background: SAGE[600], padding: "15px 30px", borderRadius: 8, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: "inherit" }}>{isAuthenticated ? <span className="inline-flex items-center gap-1.5">Continue your plan<ArrowRight className="h-4 w-4" aria-hidden="true" /></span> : "Start your plan"}</button>
            <button onClick={scrollTo("how-it-works")} style={{ fontSize: 16, color: WARM.charcoal, border: `1px solid ${WARM.stone}`, padding: "15px 26px", borderRadius: 8, background: "none", cursor: "pointer", fontFamily: "inherit" }}><span className="inline-flex items-center gap-1.5">See how it works<ArrowRight className="h-4 w-4" aria-hidden="true" /></span></button>
          </div>
          <div style={{ marginTop: 28, fontSize: 14, color: WARM.taupe, display: "flex", gap: 18, flexWrap: "wrap", justifyContent: "center" }}>
            <span>30-day free trial</span><span style={{ color: WARM.stone }}>·</span><span>No credit card</span><span style={{ color: WARM.stone }}>·</span><span>Made in Canada</span>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how-it-works" style={{ background: WARM.cream, padding: "8px 32px 72px" }}>
        <div className={CONTAINER}>
          <div style={{ textAlign: "center", margin: "0 auto 28px" }}>
            <Eyebrow>How it works</Eyebrow>
          </div>
          <div className="grid gap-5 grid-cols-1 min-[560px]:grid-cols-2 min-[960px]:grid-cols-4">
            {HOW_IT_WORKS.map((step) => (
              <div key={step.num} style={{ background: WARM.white, border: "1px solid #E7E1D5", borderRadius: 14, padding: "30px 28px" }}>
                <span style={{ width: 40, height: 40, borderRadius: "50%", background: SAGE[50], color: SAGE[600], fontFamily: FONT.serif, fontSize: 16, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>{step.num}</span>
                <h3 style={{ fontFamily: FONT.serif, fontWeight: 500, fontSize: 18, lineHeight: 1.25, color: WARM.ink, margin: "0 0 9px" }}>{step.title}</h3>
                {/* Step 04 has NO body since the s216 claim removal — render no empty <p>.
                    See how-it-works-data.ts for why no replacement copy was written. */}
                {step.body && <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: "#5A554A" }}>{step.body}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TWO SIDES, ONE PLACE ============ */}
      <section style={{ background: WARM.cream, padding: "96px 32px" }}>
        <div className={CONTAINER}>
          <div style={{ textAlign: "center", maxWidth: 680, margin: "0 auto 56px" }}>
            <Eyebrow>The whole arc, in one tool</Eyebrow>
            <h2 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 40, lineHeight: 1.14, letterSpacing: "-0.01em", color: WARM.ink, margin: "0 0 18px", textWrap: "balance" }}>Two sides, one place.</h2>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: "#514D44", margin: 0 }}>
              While you're here, you organize everything your family will need. When the time comes, your executor opens the same plan and sees exactly what to do. The only product that carries your work all the way through.
            </p>
          </div>
          <div className="grid gap-6 grid-cols-1 min-[860px]:grid-cols-2">
            {[
              { eyebrow: "For planners", title: "Put it all in place — while you still can.", bullets: [<>A category-based guided flow — <strong style={{ fontWeight: 500 }}>Start Here</strong> always tells you what's next</>, "Upload a statement — it reads it and fills the details for you", "Your wishes, contacts, and documents — gathered where they'll be found"] as ReactNode[] },
              { eyebrow: "For executors", title: "No scrambling. No guesswork.", bullets: ["A guided settlement flow — every step of closing the estate, laid out clearly", "Pre-filled letters and government forms, ready to sign", "Everything the planner prepared, ready the moment it's needed"] as ReactNode[] },
            ].map((col) => (
              <div key={col.eyebrow} style={card}>
                <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: SAGE[400], marginBottom: 14 }}>{col.eyebrow}</div>
                <h3 style={{ fontFamily: FONT.serif, fontWeight: 500, fontSize: 24, lineHeight: 1.2, color: WARM.ink, margin: "0 0 18px" }}>{col.title}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {col.bullets.map((b, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                      <CheckMark />
                      <span style={{ fontSize: 15.5, lineHeight: 1.55, color: WARM.charcoal }}>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EXECUTOR-AS-BUYER BAND (clay) ============ */}
      <section style={{ background: CLAY[100], padding: "84px 32px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
          <Eyebrow color={CLAY[600]}>Doing this for a parent?</Eyebrow>
          <h2 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 36, lineHeight: 1.18, letterSpacing: "-0.01em", color: "#3A2A1C", margin: "0 0 20px", textWrap: "balance" }}>
            Get your parents organized before you have to settle their estate.
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.62, color: CLAY[800], margin: "0 auto 32px", maxWidth: 620 }}>
            Many people find this product while thinking about an aging parent. Sit down together now, and you spare yourself months of searching through drawers, calling institutions, and guessing what they would have wanted later.
          </p>
          <button onClick={go("/signup")} style={{ fontSize: 16, color: WARM.white, background: CLAY[600], padding: "15px 30px", borderRadius: 8, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Start a plan together</button>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section id="features" style={{ background: WARM.cream, padding: "96px 32px" }}>
        <div className={CONTAINER}>
          <div style={{ maxWidth: 640, margin: "0 0 52px" }}>
            <Eyebrow>What's inside</Eyebrow>
            <h2 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 40, lineHeight: 1.14, letterSpacing: "-0.01em", color: WARM.ink, margin: 0 }}>Everything in one place.</h2>
          </div>
          <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(290px,1fr))]">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} style={{ background: WARM.white, border: "1px solid #E7E1D5", borderRadius: 14, padding: "30px 28px" }}>
                <div style={{ width: 46, height: 46, borderRadius: 11, background: SAGE[50], display: "flex", alignItems: "center", justifyContent: "center", color: SAGE[600], marginBottom: 18 }}>
                  <Icon size={22} strokeWidth={1.7} />
                </div>
                <h3 style={{ fontFamily: FONT.serif, fontWeight: 500, fontSize: 20, lineHeight: 1.25, color: WARM.ink, margin: "0 0 9px" }}>{title}</h3>
                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: "#5A554A" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ESTATE READY SCORE SPOTLIGHT ============ */}
      <section style={{ background: WARM.stoneLight, padding: "92px 32px" }}>
        <div className={`${CONTAINER} grid items-center gap-[56px] grid-cols-1 min-[860px]:grid-cols-[0.92fr_1.08fr]`}>
          <div>
            <Eyebrow>How ready is it?</Eyebrow>
            <h2 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 38, lineHeight: 1.15, letterSpacing: "-0.01em", color: WARM.ink, margin: "0 0 18px" }}>A number you can act on.</h2>
            <p style={{ fontSize: 17, lineHeight: 1.62, color: "#514D44", margin: "0 0 18px" }}>
              Ten essentials make up your Estate Ready Score. As you complete them, the score rises — and the plan you're leaving behind gets a little kinder to the people who'll open it.
            </p>
            {/* "More prepared than 70% of Canadians." — REMOVED s216 (MARKETING-TRUTH-1,
                Reade's ruling). An unsourced comparative performance claim; no number in the
                stat library backs it. Nothing replaced it: the paragraph above already carries
                the section's point without a statistic. */}
          </div>
          <div style={{ background: WARM.white, border: "1px solid #E2DCCE", borderRadius: 18, boxShadow: "0 30px 70px -38px rgba(31,40,30,0.3)", padding: 30 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 22, marginBottom: 24, paddingBottom: 24, borderBottom: "1px solid #EEE8DC" }}>
              <DonutRing size={86} strokeWidth={8} withOfTen />
              <div>
                <div style={{ fontFamily: FONT.serif, fontSize: 21, fontWeight: 500, color: WARM.ink, marginBottom: 4 }}>Looking good.</div>
                <div style={{ fontSize: 14.5, lineHeight: 1.5, color: "#5A554A" }}>Three essentials left. Here's what's still missing for your executor.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 min-[420px]:grid-cols-2" style={{ rowGap: 11, columnGap: 22 }}>
              {SPOTLIGHT_ESSENTIALS.map((row) => (
                <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 14, color: row.done ? WARM.charcoal : WARM.taupe }}>
                  {row.done ? <DoneBadge d={17} check={10} strokeWidth={3.4} /> : <TodoDot d={17} color="#C8862E" />}
                  {row.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHEN THE TIME COMES — settlement ============ */}
      <section style={{ background: SAGE[50], padding: "92px 32px" }}>
        <div className={`${CONTAINER} grid items-center gap-[56px] grid-cols-1 min-[860px]:grid-cols-[1.08fr_0.92fr]`}>
          {/* settlement mock */}
          <div style={{ background: WARM.white, border: `1px solid ${SAGE[100]}`, borderRadius: 18, boxShadow: "0 30px 70px -38px rgba(31,40,30,0.3)", padding: 28 }}>
            <div style={{ paddingBottom: 20, borderBottom: "1px solid #EEE8DC", marginBottom: 20 }}>
              <div style={{ fontFamily: FONT.serif, fontSize: 21, fontWeight: 500, color: WARM.ink, marginBottom: 6 }}>Hi Daniel.</div>
              <div style={{ fontSize: 15, lineHeight: 1.55, color: "#5A554A" }}>Here's what your mother put in place, and what's next. Take your time.</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 9 }}>
              <span style={{ fontSize: 13.5, fontWeight: 500, color: SAGE[800] }}>Settling the estate</span>
              <span style={{ fontSize: 13, color: WARM.taupe }}>4 of 18 steps done</span>
            </div>
            <div style={{ height: 7, borderRadius: 99, background: "#E4E9DC", marginBottom: 22, overflow: "hidden" }}>
              <div style={{ height: "100%", width: "22%", background: SAGE[600], borderRadius: 99 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11, fontSize: 14.5, color: WARM.charcoal }}>
                <DoneBadge d={20} check={11} />Register the death & order certificates
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 11, fontSize: 14.5, color: WARM.charcoal }}>
                <DoneBadge d={20} check={11} />Locate the will <span style={{ color: WARM.taupe }}>&nbsp;— saved here</span>
              </div>
              {/* NOW row */}
              <div style={{ display: "flex", alignItems: "center", gap: 11, fontSize: 14.5, color: SAGE[800], background: SAGE[50], border: `1px solid ${SAGE[100]}`, borderRadius: 10, padding: "11px 13px", margin: "1px 0" }}>
                <span style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${SAGE[600]}`, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: SAGE[600] }} />
                </span>
                <span style={{ fontWeight: 500 }}>Notify the authorities</span>
                <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: SAGE[600] }}>Now</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 11, fontSize: 14.5, color: WARM.taupe }}>
                <TodoDot d={20} width={1.7} />Apply for probate <span style={{ color: "#B8AE9B" }}>&nbsp;— form pre-filled</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 11, fontSize: 14.5, color: WARM.taupe }}>
                <TodoDot d={20} width={1.7} />Notify banks & close accounts
              </div>
            </div>
            <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 10, background: "#FBF9F4", border: "1px solid #EEE8DC", borderRadius: 11, padding: "12px 14px", fontSize: 13.5, color: "#5A554A" }}>
              <span style={{ color: SAGE[600], flex: "none", display: "flex" }}><Heart size={16} strokeWidth={1.8} /></span>
              Everything your mother organized is already here.
            </div>
          </div>
          {/* copy */}
          <div>
            <Eyebrow>When the time comes</Eyebrow>
            <h2 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 38, lineHeight: 1.15, letterSpacing: "-0.01em", color: WARM.ink, margin: "0 0 18px", textWrap: "balance" }}>Your executor isn't left guessing.</h2>
            <p style={{ fontSize: 17, lineHeight: 1.62, color: "#514D44", margin: "0 0 26px" }}>
              The hardest week of someone's life shouldn't start with a search through drawers and a stack of institutions to call. When you've planned here, your executor opens a guided path — one clear step at a time, with everything you organized already waiting.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {EXECUTOR_POINTS.map(({ icon: Icon, title, body }) => (
                <div key={title} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <span style={{ width: 38, height: 38, borderRadius: 10, background: WARM.white, border: `1px solid ${SAGE[100]}`, display: "flex", alignItems: "center", justifyContent: "center", color: SAGE[600], flex: "none" }}>
                    <Icon size={19} strokeWidth={1.7} />
                  </span>
                  <div>
                    <div style={{ fontSize: 15.5, fontWeight: 500, color: WARM.ink, marginBottom: 3 }}>{title}</div>
                    <div style={{ fontSize: 14.5, lineHeight: 1.55, color: "#5A554A" }}>{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRICING ============
          Extracted to PricingSection (s216) so "/pricing" renders the SAME markup — one
          definition, two URLs.
          🔴 s217 (D-217.3): the tier cards and the prices are DOWN until the pricing reset, and
          StructuredData no longer publishes Offers. The section still renders — heading, one
          line, and the trial call-to-action — so the page keeps its shape.
      */}
      <PricingSection onStart={() => navigate(isAuthenticated ? "/dashboard" : "/signup")} />

      {/* ============ ADVISOR BAND (B2B) ============
          Extracted to AdvisorSection (s216), shared with the "/advisors" route. The
          SHOW_ADVISOR_BAND flag still gates the IN-PAGE band only — see AdvisorSection's header
          for why the route itself is deliberately not gated.
      */}
      {SHOW_ADVISOR_BAND && (
        <AdvisorSection
          onPartner={() => navigate("/contact")}
          onSeePricing={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />
      )}

      {/* ============ TESTIMONIALS — REMOVED s216 ============
          MARKETING-TRUTH-1, Reade's ruling s216: the whole block went, not just the
          "Placeholder testimonials — to be replaced at launch." disclaimer under the heading.
          The three quotes (Maureen L. / Susan & David R. / James P.) were invented placeholders,
          and invented testimonials on a live page are a credibility and Competition Act problem,
          not an SEO one. The heading they sat under ("Trusted by families and the people who
          advise them.") went with them: a trust heading over no evidence is the same claim.
          Restore this section when REAL, attributable quotes exist — not before.
      */}

      {/* ============ FINAL CTA (clay) ============ */}
      <section style={{ background: `radial-gradient(120% 100% at 50% 0%, ${CLAY[100]} 0%, ${WARM.cream} 70%)`, padding: "104px 32px 96px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 48, lineHeight: 1.1, letterSpacing: "-0.015em", color: WARM.ink, margin: "0 0 22px", textWrap: "balance" }}>Start with one hour.</h2>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: CLAY[800], margin: "0 auto 36px", maxWidth: 520 }}>
            It takes an afternoon. It gives your family one less hard thing in their hardest week.
          </p>
          <button onClick={go("/signup")} style={{ display: "inline-block", fontSize: 16, color: WARM.white, background: CLAY[600], padding: "16px 34px", borderRadius: 8, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Start your plan</button>
          <div style={{ marginTop: 24, fontSize: 14, color: WARM.taupe }}>30-day free trial · No credit card · Cancel anytime</div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
